import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import useSessionStore from '@/stores/useSessionStore'

import { User, userFragment } from './useUserApi'

export interface UpdateUserResult {
  errors: { field: string; message: string }[]
  message: string
  success: boolean
  user: User
}

export interface UpdateUserInput {
  clubName?: string
  countryCode?: string
  email?: string
  firstName?: string
  guest_id?: string
  lastName?: string
  mobileVerified?: boolean
  phoneNumber?: string
  smsTrackingOptin?: boolean
  smsTrackingPhoneNumber?: string
  smsTrackingTermsAccepted?: boolean
}

export const useUpdateUserApi = (): UseMutationResult<UpdateUserResult, Error, UpdateUserInput> => {
  const client = useGraphQLClient()
  const queryClient = useQueryClient()
  const userId = useSessionStore((state) => state.userId)

  const query = gql`
    mutation UpdateUser($input: UpdateUserInput!) {
      updateUser(input: $input) {
        errors {
          field
          message
        }
        message
        success
        user {
          ...user
        }
      }
    }
    ${userFragment}
  `

  const mutationFn = (input: UpdateUserInput): Promise<UpdateUserResult> =>
    client
      .request<{ updateUser: UpdateUserResult }>(query, { input })
      .then(({ updateUser }) => updateUser)

  return useMutation<UpdateUserResult, Error, UpdateUserInput>({
    mutationFn,
    mutationKey: ['user', userId],
    // A phone change is the other in-session moment the backend may tie an
    // SMS opt-in made while logged out to this account: it matches on the
    // account's phone number when the account reads its own subscription
    // (GSE-12330). Refetch that read now rather than on the next page load.
    onSuccess: (data, variables) => {
      if (variables.phoneNumber !== undefined && data.success) {
        void queryClient.invalidateQueries({ queryKey: ['smsSubscription'] })
      }
    },
  })
}
